import { Trash } from '@phosphor-icons/react';
import { cloneElement, ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLogActions } from 'bom/hooks';
import { ILog } from 'bom/types';
import { Button, Confirmation } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { useAgGridClientTableContext } from 'core/states';
import { Color, Variant } from 'theme/enums';

interface IProps {
  logs: ILog[];
  buttonElement?: ReactElement;
  buttonLabel?: string;
  notificationPosition?: NotificationPosition;
}

export const RemoveLogsButton = ({
  logs,
  buttonElement,
  buttonLabel,
  notificationPosition,
}: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const { t } = useTranslation();

  const { updateRows } = useAgGridClientTableContext();

  const { removeLogs } = useLogActions({ notificationPosition });

  const onRemoveSuccess = useCallback(async () => updateRows({ remove: logs }), [logs, updateRows]);

  const onRemove = useCallback(async () => {
    setIsConfirmationOpen(false);

    const removedAccessIds = logs.map((log) => log.id);

    const { success } = await removeLogs({
      ids: removedAccessIds,
    });

    if (!success) {
      return;
    }

    onRemoveSuccess();
  }, [onRemoveSuccess, logs, removeLogs]);

  const button = useMemo(() => {
    if (buttonElement) {
      return cloneElement(buttonElement, {
        onClick: () => setIsConfirmationOpen(true),
      });
    }

    return (
      <Button
        text={buttonLabel ?? t('buttonLabelRemoveLogs', { count: logs.length })}
        color={Color.DANGER}
        variant={Variant.SOFT}
        startDecorator={<Trash />}
        onClick={() => setIsConfirmationOpen(true)}
      />
    );
  }, [buttonElement, buttonLabel, t, logs.length]);

  return (
    <>
      {button}

      <Confirmation
        isOpen={isConfirmationOpen}
        title={t('titleRemoveLogs', {
          count: logs.length,
        })}
        message={t('messageConfirmRemoveLogs', {
          count: logs.length,
        })}
        confirmBtnLabel={t('buttonLabelRemoveLogs', { count: logs.length })}
        confirmBtnColor={Color.DANGER}
        onConfirm={onRemove}
        onCancel={() => setIsConfirmationOpen(false)}
      />
    </>
  );
};
